import { getPriceWithVAT, roundNumber } from "helpers";
import { getPackagePrice } from "helpers/electricity-helper";
import { ContractPeriodType, ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP, ElectricityPackage, ElectricityPackageBaseFixationType, ElectricityPackageMeterType } from "models/electricity/new-electricity-agreement";
import React, { useContext, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { AuthenticationContext } from "services/context/authentication.context";
import moment from "moment";
import { dateFormat } from "common/dateFormatterConfig";
import { isNotNullOrUndefined } from "common/data-util";
import { PRICE_ROUNDING_SCALE, SUM_ROUNDING_SCALE } from "common/constants";
import { getBaseConsumptionProportionScale } from "components/Agreements/ElectricityAgreementCard";

interface NewElectricityAgreementPackagesProps {
    consumptionPackages: ElectricityPackage[];
    productionPackages: ElectricityPackage[];
    selectPackages: (consumptionPackage: ElectricityPackage, productionPackage?: ElectricityPackage) => void;
    selectedPackage?: ElectricityPackage;
    loading: boolean;
    showResaleOffer: boolean;
    isCompany: boolean;
}

export const getProductionPackageWithQuarterHourMeterType = (productionPackages?: ElectricityPackage[]) => {
    return productionPackages?.find(p => p.meterType === ElectricityPackageMeterType.QUARTER_HOUR);
}

export default function NewElectricityAgreementPackages({ 
    consumptionPackages, 
    productionPackages,
    selectPackages, 
    selectedPackage, 
    loading, 
    showResaleOffer, 
    isCompany
}: NewElectricityAgreementPackagesProps) {
    const [consumptionPackage, setConsumptionPackage] = useState<ElectricityPackage>();

    const basicPackages = consumptionPackages.filter((pckg) => pckg.jointInvoicePackage === false);
    const filteredPackages = basicPackages.filter(pckg => pckg.showToBusinessCustomer);

    return (
        <div className="new-electricity-agreement__container">
            <div className="d-flex justify-content-between align-items-center">
                <h1>Valige kõige sobilikum pakett</h1>
            </div>
            <div className="new-electricity-agreement__package-container mt-5">
                { loading ? 
                    <div className='d-flex justify-content-center mt-5'>
                        <Spinner animation="grow" variant="primary" />
                    </div> : null}
                { (filteredPackages).map(pckg => 
                    <ElectricityPackageCard 
                        key={pckg.id}
                        electricityPackage={pckg}
                        selectPackages={selectPackages}
                        selected={pckg.id === selectedPackage?.id}
                        showDescription={() => setConsumptionPackage(pckg)}
                        showResaleOffer={showResaleOffer}
                        productionPackage={getProductionPackageWithQuarterHourMeterType(productionPackages)}
                        isCompany={isCompany}
                    />
                )}
            </div>
            <ElectricityPackageDescriptionModal
                consumptionPackage={consumptionPackage}
                productionPackage={getProductionPackageWithQuarterHourMeterType(productionPackages)}
                onHide={() => setConsumptionPackage(undefined)}
                selectPackages={selectPackages}
            />
        </div>
    );
}

interface ElectricityPackageCardProps  { 
    electricityPackage: ElectricityPackage, 
    showDescription: any, 
    selectPackages: (consumptionPackage: ElectricityPackage, productionPackage?: ElectricityPackage) => void 
    selected: boolean 
    showResaleOffer: boolean;
    productionPackage?: ElectricityPackage;
    isCompany: boolean;
}

function ElectricityPackageCard({ 
    electricityPackage, 
    showDescription, 
    selectPackages, 
    selected, 
    showResaleOffer,
    productionPackage,
    isCompany
} : ElectricityPackageCardProps
) {

    const { state } = useContext(AuthenticationContext);
    const isVatLiable = isCompany && !!state.customer.vatRegistryNumber;

    const className = 'new-electricity-agreement__package-card' + (electricityPackage.highlighted ? ' new-electricity-agreement__package-card--highlighted' : '')

    const price = getPackagePrice(electricityPackage.basePriceCentsKwh, electricityPackage.basePriceTextEt, electricityPackage.marginal, isCompany);

    let marketPrice: number | undefined = electricityPackage.averageMarketPrice;
    if (marketPrice) {
        marketPrice = (marketPrice / 10);
    }

    const lastMonthWithLastDay = moment().subtract(1, 'months').startOf('month');

    return (
        <div className={className}>
            <div className="new-electricity-agreement__package-card__name">{ electricityPackage.nameEt }</div>
            <div className="new-electricity-agreement__package-card__price">{ price }</div>
            { electricityPackage.basePriceCentsKwh ? <div className="new-electricity-agreement__package-card__price-desc">{ electricityPackage.basePriceTextEt }</div> : null}
            <div className="h-75">
                <div className="row mt-2 u-fs-16">
                    <div className="col">
                        <strong>{ electricityPackage.basePriceDescriptionEt}</strong>
                    </div>
                </div>
                { electricityPackage.greenEnergy ?
                    <div className="row mt-3 u-fs-14">
                        <div className="col"><div className="new-electricity-agreement__package-card__green-energy">Roheteenus</div></div>
                    </div>
                    : null 
                }
                {
                    electricityPackage.baseFixationType !== null && (
                        <div className="row mt-1 u-fs-14">
                            <div className="col text-gray-700">
                                Fikseerimise tüüp:
                            </div>
                            <div className="col text-right">
                                <strong>{ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP[electricityPackage.baseFixationType!]}</strong>
                            </div>
                        </div>
                    )
                }
                {
                    isNotNullOrUndefined(electricityPackage.baseConsumptionProportion) && (
                        <div className="row mt-1 d-flex align-items-center u-fs-14">
                            <div className="col text-gray-700">
                                Fikseeritud tarbimise osakaal:
                            </div>
                            <div className="col text-right">
                                <strong>{roundNumber(electricityPackage.baseConsumptionProportion, getBaseConsumptionProportionScale(electricityPackage.baseFixationType!))} {electricityPackage.baseFixationType === ElectricityPackageBaseFixationType.PERCENTAGE ? '%' : 'MWh'}</strong>
                            </div>
                        </div>
                    )
                }
                { electricityPackage.marketPricePackage && marketPrice ?
                    <div className="row mt-3 u-fs-14">
                        <div className="col text-gray-700">
                            Eelmise kuu keskmine börsihind:
                        </div>
                        <div className="col text-right">
                        <strong>{ isCompany ? roundNumber(marketPrice, PRICE_ROUNDING_SCALE) : getPriceWithVAT(marketPrice, PRICE_ROUNDING_SCALE, lastMonthWithLastDay) } s/kWh</strong>
                        </div>
                    </div>
                    : null }
                <div className="row mt-3 d-flex align-items-center u-fs-14">
                    <div className="col text-gray-700">
                        {electricityPackage.dynamicPackage ? "Börsihinnaga tarbimise marginaal:" : "Marginaal:"}
                    </div>
                    <div className="col text-right">
                        <strong>{ isCompany ? (electricityPackage.marginal ? roundNumber(electricityPackage.marginal, PRICE_ROUNDING_SCALE) : 0) : getPriceWithVAT(electricityPackage.marginal, PRICE_ROUNDING_SCALE, moment()) } s/kWh</strong>
                    </div>                
                </div>
                <div className="row mt-1 u-fs-14">
                    <div className="col text-gray-700">
                        Lepinguperiood:
                    </div>
                    <div className="col text-right">
                        <strong>{ electricityPackage.contractPeriod?.name }</strong>
                    </div>
                </div>
                {
                    (electricityPackage.contractPeriod?.type === ContractPeriodType.TEMPORARY && electricityPackage.contractEndDate) ? (
                        <div className="row mt-1 u-fs-14">
                            <div className="col text-gray-700">
                                Lepingu lõpp:
                            </div>
                            <div className="col text-right">
                                <strong>{ moment(electricityPackage.contractEndDate).format(dateFormat) }</strong>
                            </div>
                        </div>
                    ) : null
                }
                <div className="row mt-1 u-fs-14">
                    <div className="col text-gray-700">
                        Kuutasu:
                    </div>
                    <div className="col text-right">
                        <strong>{ (isCompany ? (electricityPackage.monthlyFeeEuros ? roundNumber(electricityPackage.monthlyFeeEuros, SUM_ROUNDING_SCALE) : 0) : getPriceWithVAT(electricityPackage.monthlyFeeEuros || 0, SUM_ROUNDING_SCALE, moment())) } €</strong>
                    </div>
                </div>
                <div className="row mt-3 mb-4 u-fs-14">
                    <div className="col text-gray-700">
                        { (isCompany && !isVatLiable) && `Lisandub käibemaks`}
                        { !isCompany && `Sisaldab käibemaksu`}
                    </div>
                </div>
            </div>
            {(showResaleOffer && productionPackage) && (
                <div className="new-electricity-agreement__package-card-resale-section">
                    <hr />
                    <div className="row mt-2 u-fs-14">
                        <div className="col">
                            <strong>Elektri võrku tagasimüük</strong>
                        </div>
                    </div>
                    <div className="text-gray-700 mb-2 u-fs-14">
                        {productionPackage.basePriceDescriptionEt}
                    </div>
                    <div className="row mt-1 u-fs-14">
                        <div className="col text-gray-700">
                            Marginaal:
                        </div>
                        <div className="col text-right">
                            <strong>{(productionPackage.marginal ? roundNumber(productionPackage.marginal, PRICE_ROUNDING_SCALE) : 0)} s/kWh</strong>
                        </div>
                    </div>
                    <div className="row mt-3 mb-4 u-fs-14">
                        <div className="col text-gray-700">
                            { (isCompany && isVatLiable) && `Hinnale lisandub käibemaks` }
                        </div>
                    </div>
                </div>
            )}
            <div className="d-flex flex-column mt-auto">
                <button className="btn btn-primary btn-lg" disabled={selected} onClick={() => selectPackages(electricityPackage, productionPackage)}>
                    { selected ? 'Pakett valitud' : 'Vali pakett' }
                </button>
                <button className="btn btn-outline-primary btn-lg mt-2" onClick={showDescription}>
                    Loe rohkem
                </button>
            </div>
        </div>
    );
}

function ElectricityPackageDescriptionModal(
    { consumptionPackage, productionPackage, onHide, selectPackages }: { 
        consumptionPackage?: ElectricityPackage, 
        productionPackage?: ElectricityPackage, 
        onHide: any, 
        selectPackages: (consumptionPackage: ElectricityPackage, productionPackage?: ElectricityPackage,) => void 
    }
) {

    return (
        <Modal
            show={!!consumptionPackage}
            onHide={onHide}
            backdropClassName="new-electricity-agreement__package-description__backdrop"
            dialogClassName="new-electricity-agreement__package-description__container"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {consumptionPackage?.nameEt}
                </Modal.Title>
            </Modal.Header>            
            <Modal.Body>
                <div className="d-flex">
                    <div className="new-electricity-agreement__package-description__text" dangerouslySetInnerHTML={{ __html: consumptionPackage?.descriptionEt || '' }}></div>
                </div>
                <div className="mt-5 pt-5">
                    <button className="btn btn-primary btn-lg" onClick={() => selectPackages(consumptionPackage!, productionPackage)}>Vali pakett</button>
                </div>
            </Modal.Body>
        </Modal>        
    )
}